import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ChevronRight,
  CloudUpload,
  FileBadge2,
  FilePlus2,
  FileText,
  RotateCw,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Badge, Card, EmptyState, Skeleton } from "@/components/ui";
import { RequestStatusBadge } from "@/components/RequestStatusBadge";
import { certificateRequestsApi, type CustomerCertificateRequest } from "@/lib/api";
import { requestTypeLabel, validityLabel } from "@/lib/certificate-requests";
import { formatDate } from "@/lib/format";

function greeting(t: ReturnType<typeof useT>["t"]) {
  const h = new Date().getHours();
  if (h < 12) return t("dash.greeting_morning");
  if (h < 19) return t("dash.greeting_afternoon");
  return t("dash.greeting_evening");
}

export function Dashboard() {
  const { t } = useT();
  const auth = useAuth();
  const { data } = useData();

  // Canonical state lives on the server (PC-27) — the dashboard reads the
  // customer's real requests instead of a browser-local mock.
  const q = useQuery({
    queryKey: ["me", "certificate-requests"],
    queryFn: certificateRequestsApi.list,
  });
  const requests = q.data?.requests ?? [];

  const firstName =
    data.profile.firstName ||
    (auth.status === "authenticated" ? `+${auth.user.phone}` : "");

  const issued = requests.filter((r) => r.status === "issued");
  const inProgress = requests.filter((r) => r.status === "pending" || r.status === "verified");
  const documentCount = requests.reduce((n, r) => n + r.artifacts.length, 0);
  const recent = requests.slice(0, 4);

  const stats = [
    { key: "active", label: t("dash.stat_active"), value: issued.length, tone: "text-emerald-700 bg-emerald-50" },
    { key: "pending", label: t("dash.stat_in_progress"), value: inProgress.length, tone: "text-brand-700 bg-brand-50" },
    { key: "docs", label: t("dash.stat_documents"), value: documentCount, tone: "text-slate-700 bg-slate-100" },
  ];

  return (
    <div className="space-y-8">
      <header>
        <p className="text-sm font-medium text-slate-500">
          {greeting(t)}, {firstName} 👋
        </p>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          {t("dash.welcome_subtitle")}
        </h1>
      </header>

      <section className="grid grid-cols-3 gap-3 sm:gap-4">
        {stats.map((s) => (
          <Card key={s.key} className="p-4 sm:p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{s.label}</p>
            <div className="mt-2 flex items-end justify-between gap-3">
              <span className="text-3xl font-semibold text-slate-900">{q.isPending ? "—" : s.value}</span>
              <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${s.tone}`}>
                <FileBadge2 className="h-4 w-4" />
              </span>
            </div>
          </Card>
        ))}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("dash.quick_actions")}
        </h2>
        <div className="grid gap-3 md:grid-cols-3">
          <QuickAction
            to="/solicitar"
            icon={<FilePlus2 className="h-5 w-5" />}
            title={t("dash.qa_new_cert")}
            desc={t("dash.qa_new_cert_desc")}
            accent="from-brand-600 to-brand-700 text-white"
          />
          <QuickAction
            to="/documentos"
            icon={<CloudUpload className="h-5 w-5" />}
            title={t("dash.qa_upload")}
            desc={t("dash.qa_upload_desc")}
          />
          <QuickAction
            to="/certificados"
            icon={<RotateCw className="h-5 w-5" />}
            title={t("dash.qa_renew")}
            desc={t("dash.qa_renew_desc")}
          />
        </div>
      </section>

      <section>
        <header className="mb-4 flex items-end justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">{t("dash.your_certs")}</h2>
          <Link
            to="/certificados"
            className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
          >
            {t("dash.view_all")}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </header>

        {q.isPending ? (
          <div className="grid gap-3">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        ) : q.isError ? (
          <Card className="p-5 text-sm text-red-700">
            {q.error instanceof Error ? q.error.message : t("onb.err_unknown")}
          </Card>
        ) : recent.length === 0 ? (
          <EmptyState
            icon={<FileBadge2 className="h-6 w-6" />}
            title={t("dash.empty_title")}
            body={t("dash.empty_body")}
            action={
              <Link to="/solicitar" className="btn-primary">
                <FilePlus2 className="h-4 w-4" />
                {t("dash.start_first")}
              </Link>
            }
          />
        ) : (
          <div className="grid gap-3">
            {recent.map((r) => (
              <RequestRow key={r.id} request={r} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function RequestRow({ request }: { request: CustomerCertificateRequest }) {
  const { t, locale } = useT();
  return (
    <Link
      to={`/certificados/solicitudes/${request.id}`}
      className="card group flex items-center gap-4 p-5 transition hover:-translate-y-0.5 hover:shadow-card"
    >
      <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-700">
        <FileText className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <RequestStatusBadge status={request.status} />
          {request.invoice && <Badge tone="green">{request.invoice.number}</Badge>}
          <span className="text-xs text-slate-500">{formatDate(request.updatedAt, locale)}</span>
        </div>
        <p className="mt-2 truncate text-base font-semibold text-slate-900">
          {requestTypeLabel(t, request.type)}
        </p>
        <p className="mt-1 text-sm text-slate-600">
          {t("cert.class")} {request.class} · {validityLabel(t, request.validityMonths)}
        </p>
      </div>
      <ChevronRight className="h-5 w-5 flex-none text-slate-400 transition group-hover:text-slate-600" />
    </Link>
  );
}

function QuickAction({
  to,
  icon,
  title,
  desc,
  accent = "bg-white",
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
  accent?: string;
}) {
  const isDark = accent.startsWith("from-");
  return (
    <Link
      to={to}
      className={
        "group relative flex flex-col gap-4 rounded-2xl border border-slate-200/80 p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-card " +
        (isDark ? `bg-gradient-to-br ${accent}` : "bg-white")
      }
    >
      <span
        className={
          "inline-flex h-10 w-10 items-center justify-center rounded-xl " +
          (isDark ? "bg-white/15 text-white" : "bg-brand-50 text-brand-700")
        }
      >
        {icon}
      </span>
      <div>
        <p className={"text-base font-semibold " + (isDark ? "text-white" : "text-slate-900")}>{title}</p>
        <p className={"mt-1 text-sm " + (isDark ? "text-brand-100" : "text-slate-600")}>{desc}</p>
      </div>
      <ArrowUpRight
        className={
          "absolute right-5 top-5 h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 " +
          (isDark ? "text-white/80" : "text-slate-400")
        }
      />
    </Link>
  );
}
